import { z } from 'zod';

import { clientResourceSchema } from '@/entities/client/types';
import { propertyResourceSchema } from '@/entities/property/types';

/**
 * Opdrachten, planning en wat erbij hoort, zoals de API het over de lijn stuurt.
 *
 * Elk schema volgt één API Resource of één contractblok uit
 * `smartkeur-api/docs/02-api-contract.md` §Opdrachten, objecten, planning. De
 * schermen zien deze vorm nooit; `model.ts`, `planning.ts` en `dashboard.ts`
 * vertalen hem. Identiteit is altijd een `uuid`, verwijzingen `<relatie>_uuid`.
 */

/** ISO-8601 met tijdzone. */
const timestamp = z.string();

/** Bedrag als string met twee decimalen: `"1450.00"`. */
const amount = z.string();

export const ORDER_STATUS_CODES = [
    'new',
    'confirmed',
    'scheduled',
    'surveying',
    'draft_report',
    'completed',
    'invoiced',
    'cancelled',
] as const;

export type OrderStatusCode = (typeof ORDER_STATUS_CODES)[number];

export const orderStatusCodeSchema = z.enum(ORDER_STATUS_CODES);

const personSummarySchema = z.object({ uuid: z.string(), name: z.string() });

/** (`OrderServiceResource.inspection_type`) en (`Catalog\InspectionTypeResource`) samen. */
export const inspectionTypeSummarySchema = z.object({
    uuid: z.string(),
    code: z.string(),
    name: z.string(),
    color: z.string().nullable(),
});

/**
 * Hoe een dienst bij de klant komt (`Inspections\Enums\ServiceDelivery`): met een opname
 * (`survey`), in het rapport (`report`, de NHG-bijlage) of buiten Smartkeur, klaar te melden
 * (`manual`, zoals een energielabel zonder vragenlijst).
 */
export const SERVICE_DELIVERIES = ['survey', 'report', 'manual'] as const;

/** (`Inspections\OrderServiceResource`) een dienstregel; heeft geen eigen identiteit. */
export const orderServiceResourceSchema = z.object({
    inspection_type: inspectionTypeSummarySchema.nullable(),
    template_version_uuid: z.string().nullable(),
    status: z.string(),
    status_label: z.string(),
    sort_order: z.number(),
    /* Ontbreken bij een server van vóór deze velden; dan was elke dienst een opname. */
    delivery: z.enum(SERVICE_DELIVERIES).optional(),
    can_complete: z.boolean().optional(),
    inspection: z
        .object({
            uuid: z.string(),
            status: z.string(),
            status_label: z.string(),
            has_questionnaire: z.boolean().optional(),
            started_at: timestamp.nullable(),
            completed_at: timestamp.nullable(),
            submitted_at: timestamp.nullable(),
            /* Ontbreekt bij een server van vóór dit veld; dan mag niemand heropenen. */
            can_reopen: z.boolean().optional(),
        })
        .nullable(),
    /* Alleen voor wie geld mag zien; voor een inspecteur ontbreken ze. */
    price: amount.nullable().optional(),
    inspector_fee: amount.nullable().optional(),
    credits: z.number().nullable().optional(),
});

export type OrderServiceResource = z.infer<typeof orderServiceResourceSchema>;

/** (`Inspections\OrderResource`) */
export const orderResourceSchema = z.object({
    uuid: z.string(),
    number: z.string(),
    status: orderStatusCodeSchema,
    status_label: z.string(),
    allowed_transitions: z.array(z.string()),
    source: z.string(),
    client_uuid: z.string(),
    property_uuid: z.string(),
    assigned_user_uuid: z.string().nullable(),
    requested_at: timestamp.nullable(),
    scheduled_at: timestamp.nullable(),
    scheduled_end_at: timestamp.nullable(),
    completed_at: timestamp.nullable(),
    cancelled_at: timestamp.nullable(),
    cancelled_reason: z.string().nullable(),
    notes: z.string().nullable(),
    client: clientResourceSchema,
    property: propertyResourceSchema,
    assigned_user: personSummarySchema.nullable(),
    services: z.array(orderServiceResourceSchema),
    created_at: timestamp.nullable(),
    updated_at: timestamp.nullable(),
    /** Alleen bij één keuring: het rapport is bij de klant, dus het adres ligt vast voor jou. */
    address_locked: z.boolean().optional(),
    /* Geld is rolgebonden: voor `inspector` ontbreken deze velden. */
    price: amount.nullable().optional(),
    inspector_fee: amount.nullable().optional(),
    vat_rate: amount.nullable().optional(),
    margin: amount.nullable().optional(),
    credits_reserved: z.number().nullable().optional(),
    credits_consumed: z.number().nullable().optional(),
    internal_notes: z.string().nullable().optional(),
});

export type OrderResource = z.infer<typeof orderResourceSchema>;

/**
 * `POST /orders/{order}/start` — de keuring zoals hij nu is, plus de opname om te openen.
 *
 * De opname staat in `meta` en niet in de keuring, omdat hij het antwoord op deze ene
 * vraag is: waar ga je heen? De keuring eromheen is dezelfde als elke andere schrijfactie
 * teruggeeft, zodat de lijst waarop de knop stond meteen klopt.
 */
export const startedSurveyEnvelopeSchema = z.object({
    data: orderResourceSchema,
    meta: z.object({ inspection_uuid: z.string() }),
});

/** `GET /orders/{order}/timeline` (`Inspections\OrderTimelineResource`) */
export const orderTimelineResourceSchema = z.object({
    order_uuid: z.string(),
    events: z.array(
        z.object({
            type: z.string(),
            label: z.string(),
            occurred_at: timestamp.nullable(),
            actor: personSummarySchema.nullable(),
            automatic: z.boolean(),
        }),
    ),
});

export type OrderTimelineResource = z.infer<typeof orderTimelineResourceSchema>;

/** `GET /orders/{order}/documents` - één document bij het dossier. */
export const orderDocumentResourceSchema = z.object({
    uuid: z.string(),
    kind: z.string(),
    name: z.string(),
    meta: z.string().nullable(),
    status: z.string().nullable(),
    status_label: z.string().nullable(),
    mime_type: z.string().nullable(),
    size_bytes: z.number().nullable(),
    created_at: timestamp.nullable(),
    download_url: z.string().nullable(),
    thumbnail_url: z.string().nullable(),
});

export type OrderDocumentResource = z.infer<typeof orderDocumentResourceSchema>;

/** Het antwoord is niet gepagineerd; `meta` zegt hoe lang de links geldig zijn. */
export const orderDocumentsEnvelopeSchema = z.object({
    data: z.array(orderDocumentResourceSchema),
    meta: z.object({ url_expires_in: z.number().optional() }).optional(),
});

/** (`Catalog\InspectionTypeResource`) een dienst die dit bedrijf levert. */
export const inspectionTypeResourceSchema = inspectionTypeSummarySchema.extend({
    description: z.string().nullable(),
    is_system: z.boolean(),
    is_platform: z.boolean(),
    sort_order: z.number(),
    /** Krijgt deze dienst een eigen regel op de factuur? Alleen met een eigen tarief. */
    billed_separately: z.boolean().optional(),
    /** Wat dat tarief kost; alleen voor wie de tarieven van het bedrijf mag zien. */
    price: z.string().nullable().optional(),
});

export type InspectionTypeResource = z.infer<typeof inspectionTypeResourceSchema>;

/* -------------------------------------------------------------------------- */
/* Planning                                                                    */
/* -------------------------------------------------------------------------- */

/** (`PlanningWeekResource.suggestions[]`) */
export const planningSuggestionResourceSchema = z.object({
    id: z.string(),
    order_uuid: z.string(),
    order_number: z.string(),
    city: z.string().nullable(),
    current_scheduled_at: timestamp.nullable(),
    proposed_scheduled_at: timestamp.nullable(),
    current_inspector_uuid: z.string().nullable(),
    proposed_inspector_uuid: z.string().nullable(),
    saved_kilometres: z.number(),
    label: z.string(),
});

export type PlanningSuggestionResource = z.infer<typeof planningSuggestionResourceSchema>;

/** (`PlanningWeekResource.days[].stops[]`) */
export const planningStopResourceSchema = z.object({
    order_uuid: z.string(),
    order_number: z.string(),
    status: z.string(),
    scheduled_at: timestamp,
    scheduled_end_at: timestamp.nullable(),
    duration_minutes: z.number().nullable(),
    address: z.string(),
    city: z.string().nullable(),
    postcode: z.string().nullable(),
    client_name: z.string().nullable(),
    client_phone: z.string().nullable(),
    services: z.array(
        z.object({ code: z.string(), name: z.string(), color: z.string().nullable() }),
    ),
    distance_from_previous_km: z.number().nullable(),
    drive_minutes_from_previous: z.number().nullable(),
    inspection_uuid: z.string().nullable(),
    inspection_status: z.string().nullable(),
    offline_ready: z.boolean(),
});

export type PlanningStopResource = z.infer<typeof planningStopResourceSchema>;

/** (`PlanningWeekResource.days[]`) één inspecteur op één dag. */
export const planningDayResourceSchema = z.object({
    date: z.string(),
    inspector_uuid: z.string(),
    inspector_name: z.string(),
    stop_count: z.number(),
    distance_km: z.number(),
    drive_minutes: z.number(),
    available: z.boolean(),
    stops: z.array(planningStopResourceSchema),
    suggestions: z.array(planningSuggestionResourceSchema),
});

/** (`Inspections\PlanningWeekResource`) */
export const planningWeekResourceSchema = z.object({
    from: z.string(),
    to: z.string(),
    total_distance_km: z.number(),
    unscheduled_count: z.number(),
    days: z.array(planningDayResourceSchema),
    suggestions: z.array(planningSuggestionResourceSchema),
});

export type PlanningWeekResource = z.infer<typeof planningWeekResourceSchema>;

/** (`Inspections\AutoScheduleResultResource`) */
export const autoScheduleResultResourceSchema = z.object({
    dry_run: z.boolean(),
    scheduled_count: z.number(),
    skipped_count: z.number(),
    scheduled: z.array(
        z.object({
            order_uuid: z.string(),
            order_number: z.string(),
            inspector_uuid: z.string().nullable(),
            scheduled_at: timestamp,
            added_km: z.number(),
        }),
    ),
    skipped: z.array(
        z.object({ order_uuid: z.string(), order_number: z.string(), reason: z.string() }),
    ),
});

export type AutoScheduleResultResource = z.infer<typeof autoScheduleResultResourceSchema>;

/* -------------------------------------------------------------------------- */
/* Dashboard                                                                   */
/* -------------------------------------------------------------------------- */

const weekSchema = z.object({ number: z.number(), from: z.string(), to: z.string() });

/** `GET /dashboard` */
export const dashboardResourceSchema = z.object({
    generated_at: timestamp,
    week: weekSchema,
    inspections_this_week: z.object({
        value: z.number(),
        previous: z.number().nullable(),
        delta: z.string().nullable(),
    }),
    report_turnaround: z.object({
        hours: z.number().nullable(),
        minutes: z.number().nullable(),
        average_minutes: z.number().nullable(),
        sample_size: z.number(),
        delta: z.string().nullable(),
    }),
    open_reports: z.object({ value: z.number(), waiting_over_24h: z.number() }),
    /* Alleen met `invoices.view`; anders ontbreekt de sleutel. */
    to_invoice: z.object({ amount, orders: z.number() }).optional(),
    awaiting_completion: z.array(
        z.object({
            order_uuid: z.string(),
            order_number: z.string(),
            report_uuid: z.string().nullable(),
            report_status: z.string().nullable(),
            address: z.string(),
            meta: z.string(),
            waiting_since: timestamp.nullable(),
            waiting_hours: z.number(),
        }),
    ),
});

export type DashboardResource = z.infer<typeof dashboardResourceSchema>;

/** `GET /dashboard/signals` - één signaal. */
export const dashboardSignalResourceSchema = z.object({
    key: z.string(),
    kind: z.string(),
    priority: z.string(),
    body: z.string(),
    action_label: z.string(),
    target: z.string(),
    order_uuid: z.string().nullable(),
    report_uuid: z.string().nullable(),
    suggestion_id: z.string().nullable(),
});

export type DashboardSignalResource = z.infer<typeof dashboardSignalResourceSchema>;

/** `GET /dashboard/occupancy` - één regel per inspecteur, met `meta.week`. */
export const occupancyResourceSchema = z.object({
    user_uuid: z.string(),
    name: z.string(),
    percentage: z.number().nullable(),
    planned_minutes: z.number(),
    /* Het contract noemt een getal; zonder beschikbaarheid stuurt de API `null`. */
    available_minutes: z.number().nullable(),
    appointments: z.number(),
});

export const occupancyEnvelopeSchema = z.object({
    data: z.array(occupancyResourceSchema),
    meta: z.object({ week: weekSchema.optional() }).optional(),
});

export type OccupancyResource = z.infer<typeof occupancyResourceSchema>;

/** (`Dashboard\OnboardingChecklistResource`) de eerste stappen van een nieuw bedrijf. */
export const onboardingResourceSchema = z.object({
    completed: z.boolean(),
    completed_at: z.string().nullable(),
    steps: z.array(z.object({ key: z.string(), done: z.boolean() })),
});

export type OnboardingResource = z.infer<typeof onboardingResourceSchema>;
